import React from 'react';

import { ReactComponent as RappiLogo } from 'assets/images/logos/rappi_logo_to_print.svg';
import { formatCurrency } from 'utils/CurrencyUtils';

import type { ScheduledOrderDetail } from '../domain';
import { useScheduledOrderInvoiceLang } from './hooks';
import makeClasses from './ScheduledOrderInvoice.style';

interface Props {
  detail: ScheduledOrderDetail;
}

export function ScheduledOrderInvoice({ detail }: Props) {
  const classes = makeClasses();
  const orderIdAsString = detail.orderId.toString();
  const lang = useScheduledOrderInvoiceLang({
    orderIdFirstDigits: orderIdAsString.slice(0, -4),
    orderIdLastDigits: orderIdAsString.slice(-4),
    storeName: detail.storeName,
    totalUnits: detail.products.reduce((acc, p) => acc + p.units, 0),
  });

  return (
    <article
      aria-label='scheduled-order-invoice'
      data-order-id={detail.orderId}
      className={classes.invoice}
    >
      <header className={classes.header}>
        <figure className={classes.iconsContainer}>
          <RappiLogo className={classes.rappiLogoContainer} />
        </figure>
        <div className={classes.containerClient}>
          <h1 aria-label='user-name' className={classes.userName}>
            {detail.clientName}
          </h1>
        </div>
        <h2
          aria-label='order-id'
          className={classes.orderId}
          dangerouslySetInnerHTML={{ __html: lang.orderIdTitle }}
        />
        <div className={classes.moreInfoOrder}>
          <p aria-label='store-name'>{lang.storeName}</p>
          <p aria-label='platform'>{lang.platform}</p>
          <p aria-label='created-at' className={classes.twoPartsToFlexWrap}>
            <span>{lang.createdAtLabel}</span>
            <span>{detail.createdAt.replace('T', ' ').slice(0, -3)}</span>
          </p>
          <p aria-label='place-at' className={classes.twoPartsToFlexWrap}>
            <span>{lang.placeAtLabel}</span>
            <span>{detail.placeAt.replace('T', ' ').slice(0, -3)}</span>
          </p>
        </div>
      </header>

      <main aria-label='main-section' className={classes.main}>
        <section aria-label='products-section'>
          <h1 className={classes.titleOfDetails}>
            <span>{lang.productsTitleP1}</span>
            <span>{lang.productsTitleP2}</span>
          </h1>
          <ul aria-label='product-list' className={classes.productList}>
            {detail.products.map(product => (
              <li
                key={product.id}
                data-product-id={product.id}
                className={classes.product}
              >
                <div className={classes.productDescription}>
                  <p>{product.units}x</p>
                  <p>{product.name}</p>
                  <p>{formatCurrency(product.totalPrice)}</p>
                </div>
                {product.toppings.length > 0 && (
                  <ul aria-label='topping-list' className={classes.toppingList}>
                    {_groupToppingsByCategory(product.toppings).map(
                      ({ categoryDescription, toppings }) => (
                        <li key={categoryDescription}>
                          <p className={classes.toppingCategory}>
                            {categoryDescription}
                          </p>
                          <ul>
                            {toppings.map((topping, tIdx) => (
                              <li
                                key={`${topping.id}-${tIdx}`}
                                className={classes.toppingItem}
                              >
                                <p>{`${topping.units}x ${topping.description}`}</p>
                                <p>{formatCurrency(topping.price)}</p>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section aria-label='totals-section' className={classes.totalsSection}>
          <p>{lang.totalLabel}</p>
          <p>{formatCurrency(detail.subtotal)}</p>
        </section>
      </main>

      <footer>
        {/* ! scheduled orders have no qr_information in payload; section kept for template fidelity */}
        <section aria-label='qrcode-section' hidden />

        <aside
          aria-label='checklist-section'
          className={classes.checklistSection}
        >
          <header className={classes.checklistHeader}>
            <p aria-label='user-name'>{detail.clientName}</p>
            <p
              aria-label='order-id'
              style={{ display: 'inline-flex', gap: '0.25rem' }}
              dangerouslySetInnerHTML={{ __html: lang.orderIdTitle }}
            />
            <p aria-label='store-name'>{detail.storeName}</p>
          </header>
          <h1 className={classes.checklistTitle}>
            <span>{lang.checklistTitleP1}</span>
            <span>{lang.checklistTitleP2}</span>
          </h1>
          <ul aria-label='checklist' className={classes.checklist}>
            {detail.products.map(product => (
              <li
                key={`${product.id}-checklist`}
                data-product-id={`${product.id}-checklist-item`}
              >
                <header className={classes.checklistItemDescription}>
                  <p>&#x25A1;&#x00A0;{product.units}x</p>
                  <p>{product.name}</p>
                </header>
                {product.toppings.length > 0 && (
                  <ul className={classes.checklistToppings}>
                    {product.toppings.map((topping, tIdx) => (
                      <li key={`${topping.id}-checklist-${tIdx + 1}`}>
                        {topping.units > 1 && `${topping.units}x `}
                        {topping.description}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </aside>
      </footer>
    </article>
  );
}

interface ToppingGroup {
  categoryId: number;
  categoryDescription: string;
  toppings: ScheduledOrderDetail['products'][number]['toppings'];
}

function _groupToppingsByCategory(
  toppings: ScheduledOrderDetail['products'][number]['toppings'],
): ToppingGroup[] {
  const map = new Map<number, ToppingGroup>();

  for (const topping of toppings) {
    const existing = map.get(topping.categoryId);
    if (existing) {
      existing.toppings.push(topping);
    } else {
      map.set(topping.categoryId, {
        categoryId: topping.categoryId,
        categoryDescription: topping.categoryDescription,
        toppings: [topping],
      });
    }
  }

  return Array.from(map.values());
}
